/* ****************************************************** Site visit report */
export interface SiteVisitReport {
    id: number;
    folder_id: number;
    site_visit_no: number;
    date: string;
    note: string | null;
    inspection_for: string | null;
    status: "Accepted" | "Incomplete" | "Complete";
    weather_id: number | null;
    bi_date: string | null;
    accept_by: number | null,
    generate_report: 1 | 0;
    sign_report: 1 | 0;
    sign: string | null;
    sign_date: string | null;
    sign_report_by: number | null;
    file_download_path: string | null;
    client_name: string | null;
    building_inspector_data: SiteVisitReportBiData | null;
    architecture_data: SiteVisitReportArcData | null;
    project_data: SiteVisitReportProjectData | null;
    note_list: SiteVisitReportNoteList[];
    shared_email_list?: string[];
    weather: Weather | null;
}

export const mapSiteVisitReport = (data: any): SiteVisitReport => {
    if (!data) {
        throw new Error("Invalid site visit report type");
    }

    const response: SiteVisitReport = {
        id: data?.id,
        folder_id: data?.folder_id,
        site_visit_no: data?.site_visit_no,
        date: data?.date,
        note: data?.note ?? null,
        inspection_for: data?.inspection_for ?? null,
        status: data?.status,
        weather_id: data?.weather_id ?? null,
        bi_date: data?.bi_date ?? null,
        accept_by: data?.accept_by ?? null,
        generate_report: data?.generate_report,
        sign_report: data?.sign_report,
        sign: data?.sign ?? null,
        sign_date: data?.sign_date ?? null,
        sign_report_by: data?.sign_report_by ?? null,
        file_download_path: data?.file_download_path ?? null,
        client_name: data?.client_name ?? null,
        building_inspector_data: data?.building_inspector_data ? mapSiteVisitReportBiData(data?.building_inspector_data) : null,
        architecture_data: data?.architecture_data ? mapSiteVisitReportArcData(data?.architecture_data) : null,
        project_data: data?.project_data ? mapSiteVisitReportProjectData(data?.project_data) : null,
        note_list: Array.isArray(data?.note_list) ? mapSiteVisitReportNoteLists(data?.note_list) : [],
        shared_email_list: data?.shared_email_list ?? [],
        weather: data?.weather ? mapWeather(data?.weather) : null,
    };

    return response;
};

export const mapSiteVisitReports = (data: any[]): SiteVisitReport[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid site visit report data");
    }

    return data.map(mapSiteVisitReport);
};
/* ****************************************************************** */


/* ****************************************************** Building inspector data */
export interface SiteVisitReportBiData {
    name: string | null;
    company_name: string | null;
}

export const mapSiteVisitReportBiData = (data: any): SiteVisitReportBiData => {
    if (!data) {
        throw new Error("Invalid site visit report building inspector data");
    }

    const response: SiteVisitReportBiData = {
        name: data?.name || null,
        company_name: data?.company_name || null,
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Architect data */
export interface SiteVisitReportArcData {
    name: string | null;
    company_name: string | null;
}

export const mapSiteVisitReportArcData = (data: any): SiteVisitReportArcData => {
    if (!data) {
        throw new Error("Invalid site visit report architect data");
    }

    const response: SiteVisitReportArcData = {
        name: data?.name || null,
        company_name: data?.company_name || null,
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Project data */
export interface SiteVisitReportProjectData {
    address: string | null;
    description: string | null;
}

export const mapSiteVisitReportProjectData = (data: any): SiteVisitReportProjectData => {
    if (!data) {
        throw new Error("Invalid site visit report project data");
    }

    const response: SiteVisitReportProjectData = {
        address: data?.address || null,
        description: data?.description || null,
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Site visit report note list data */
export interface SiteVisitReportNoteList {
    id: number;
    folder_id: number;
    note_no: number;
    note: string;
    is_current_site_visit_note: 1 | 0;
    parent_site_visit_no: number | null;
    status: "Satisfactory" | "Unsatisfactory" | "Request for information" | "For information";
    response_architect: SiteVisitReportArchitectResponse | null;
    response_building_inspector: SiteVisitReportBuildingInspectorResponse | null;
}

export const mapSiteVisitReportNoteList = (data: any): SiteVisitReportNoteList => {
    if (!data) {
        throw new Error("Invalid site visit report note list type");
    }

    const response: SiteVisitReportNoteList = {
        id: data?.id,
        folder_id: data?.folder_id,
        note_no: data?.note_no,
        note: data?.note,
        is_current_site_visit_note: data?.is_current_site_visit_note,
        parent_site_visit_no: data?.parent_site_visit_no ?? null,
        status: data?.status,
        response_architect: data?.response_architect ? mapSiteVisitReportArchitectResponse(data?.response_architect) : null,
        response_building_inspector: data?.response_building_inspector ? mapSiteVisitReportBuildingInspectorResponse(data?.response_building_inspector) : null,
    };

    return response;
};

export const mapSiteVisitReportNoteLists = (data: any[]): SiteVisitReportNoteList[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid site visit report note list data");
    }

    return data.map(mapSiteVisitReportNoteList);
};
/* ****************************************************************** */


/* ****************************************************** Site visit report architect response data */
export interface SiteVisitReportArchitectResponse {
    id: number;
    site_visit_id: number;
    note_id: number;
    date: string;
    response: string | null;
    file_list: ResponseFileList[];
}

export const mapSiteVisitReportArchitectResponse = (data: any): SiteVisitReportArchitectResponse => {
    if (!data) {
        throw new Error("Invalid site visit report architect response type");
    }

    const response: SiteVisitReportArchitectResponse = {
        id: data?.id,
        site_visit_id: data?.site_visit_id,
        note_id: data?.note_id,
        date: data?.date,
        response: data?.response || null,
        file_list: Array.isArray(data?.file_list) ? mapResponseFileLists(data?.file_list) : [],
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Site visit report building inspector response data */
export interface SiteVisitReportBuildingInspectorResponse {
    id: number;
    site_visit_id: number;
    note_id: number;
    date: string;
    response: string | null;
    file_list: ResponseFileList[];
}

// Single object mapping
export const mapSiteVisitReportBuildingInspectorResponse = (data: any): SiteVisitReportBuildingInspectorResponse => {
    if (!data) {
        throw new Error("Invalid site visit report building inspector response type");
    }

    const response: SiteVisitReportBuildingInspectorResponse = {
        id: data?.id,
        site_visit_id: data?.site_visit_id,
        note_id: data?.note_id,
        date: data?.date,
        response: data?.response || null,
        file_list: Array.isArray(data?.file_list) ? mapResponseFileLists(data?.file_list) : [],
    };

    return response;
};
/* ****************************************************************** */


/* ****************************************************** Response file list data */
export interface ResponseFileList {
    id: number;
    response_id: number;
    path: string | null;
    extension: string;
}

export const mapResponseFileList = (data: any): ResponseFileList => {
    if (!data) {
        throw new Error("Invalid response file list type");
    }

    const response: ResponseFileList = {
        id: data?.id,
        response_id: data?.response_id,
        path: data?.path || null,
        extension: data?.extension,
    };

    return response;
};

export const mapResponseFileLists = (data: any[]): ResponseFileList[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid response file list data");
    }

    return data.map(mapResponseFileList);
};
/* ****************************************************************** */


/* ****************************************************** Weather */
export interface Weather {
    id: number;
    country_id: number;
    weather_name: string;
}

export const mapWeather = (data: any): Weather => {
    if (!data) {
        throw new Error("Invalid weather type");
    }

    const response: Weather = {
        id: data.id,
        country_id: data.country_id,
        weather_name: data.weather_name,
    };

    return response;
}

export const mapWeathers = (data: any[]): Weather[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid weather data");
    }

    return data.map(mapWeather);
}
/* ****************************************************************** */


export interface NoteStatus {
    lable: string;
    value: string;
    colorCode: string;
    colorStandard: string;
}

export const noteStatuses: NoteStatus[] = [
    { lable: 'S', value: 'Satisfactory', colorCode: '#B9F6CA', colorStandard: 'success' },
    { lable: 'US', value: 'Unsatisfactory', colorCode: '#fcdbdb', colorStandard: 'danger' },
    { lable: 'RFI', value: 'Request for information', colorCode: '#FFEFBC', colorStandard: 'warning' },
    { lable: 'FI', value: 'For information', colorCode: '#cce5fe', colorStandard: 'info' },
]